/*
 * Lemonade Training Oy copy rights all right reserved.
 * User Settings Type Definitions (F024 — time format preference)
 *
 * Per-user application settings. Deliberately a general document with flat
 * top-level keys so future settings are added as siblings, not nested bags.
 * Client-written (the owning user only) and shape-validated in firestore.rules.
 * A new key OR a new member of any value union here needs the rules deploy
 * FIRST — the client that writes it is rejected until the rules admit it.
 */

import { Timestamp } from "firebase/firestore";
import {
  MAX_TIME_ZONE_LENGTH,
  clocksDiffer,
  isUsableTimeZone,
  zoneCity,
} from "./zoneTime";

// Runtime array is the single source of truth — the union derives from it.
export const TIME_FORMAT_PREFERENCES = ["system", "24h", "12h"] as const;

/**
 * How times of day are rendered for this user. "system" follows the device
 * (mobile: expo-localization; web: browser locale) and is the default when
 * the settings document is absent — which it is for every user until they
 * change it.
 */
export type TimeFormatPreference = (typeof TIME_FORMAT_PREFERENCES)[number];

export const TIME_ZONE_DISPLAY_PREFERENCES = ["gym", "device"] as const;

/** Which clock times are shown on: the gym's ("gym") or the phone's ("device"). */
export type TimeZoneDisplayPreference =
  (typeof TIME_ZONE_DISPLAY_PREFERENCES)[number];

export const DEFAULT_TIME_ZONE_DISPLAY: TimeZoneDisplayPreference = "gym";

/** Both mirrored by hand in firestore.rules isValidUserSettings(); a rules test pins them. */
export const DEVICE_TIME_ZONE_MAX_LENGTH = MAX_TIME_ZONE_LENGTH;
export const DEVICE_TIME_ZONE_PATTERN =
  /^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+){0,2}$/;

/**
 * Per-user settings document
 * Stored in Firestore: Users/{uid}/Settings/preferences
 *
 * Created lazily by the first client write — an absent document means every
 * setting is at its default. The collection name "Settings" is reused at a
 * different depth by Boxes/{boxID}/Settings; a collection-group query on
 * "Settings" would span both.
 */
export type UserSettings = {
  /**
   * Optional since 6.4.0: a preferences doc created by another setting's
   * first write (e.g. classmateVisibility) carries no timeFormat. Absent
   * field = "system", exactly like an absent document.
   */
  timeFormat?: TimeFormatPreference;
  /**
   * Whether classmates at the user's gym can see them (first name + last
   * initial + photo) on rosters of classes they've booked. ABSENT = VISIBLE —
   * no backfill exists anywhere; every reader (getClassmates, both clients,
   * rules tests) uses the predicate `classmateVisibility !== false`.
   */
  classmateVisibility?: boolean;
  /** Absent = "gym", like an absent document. */
  timeZoneDisplay?: TimeZoneDisplayPreference;
  /**
   * The phone's IANA zone, present only while timeZoneDisplay is "device"
   * (rules enforce it). Set by mobile only; mobile, web and the server only
   * ever delete it.
   */
  deviceTimeZone?: string;
  /** Web writes serverTimestamp(), mobile a device clock — do not narrow, never order writes by it. */
  updatedAt: Timestamp | Date;
  /** Client-asserted and forgeable — never authorization or provenance. Narrow: no server writes this. */
  updatedFrom: "mobile" | "web";
};

/**
 * Every key the preferences document may carry — the runtime twin of the
 * firestore.rules `hasOnly` list, which cannot import this package. A new
 * setting is added here AND there in one diff. The test suite pins this array
 * against `keyof UserSettings` in both directions, so type-vs-array drift is
 * caught here; the array-vs-rules mirror is still maintained BY HAND, and a
 * rules test importing this array is what would close that half.
 */
export const USER_SETTINGS_KEYS = [
  "timeFormat",
  "classmateVisibility",
  "timeZoneDisplay",
  "deviceTimeZone",
  "updatedAt",
  "updatedFrom",
] as const;

/**
 * Whether this member is visible to classmates. ABSENT = VISIBLE: an absent
 * document, an absent key, or `true` all resolve visible; only an explicit
 * `false` hides. Readers on the untypechecked side of the seam (functions/ is
 * plain JavaScript) must call this rather than re-spell the comparison — a
 * truthiness check or `=== true` silently hides every member who never
 * toggled, which is the whole population until they do.
 *
 * A non-boolean value also resolves VISIBLE: a hand-typed "false" does NOT
 * hide the member. Rules reject wrong types on client writes, but console and
 * Admin-SDK writes bypass rules — write this key as a boolean.
 */
export function isClassmateVisible(
  settings: Pick<UserSettings, "classmateVisibility"> | null | undefined,
): boolean {
  return settings?.classmateVisibility !== false;
}

export function isTimeFormatPreference(
  value: unknown,
): value is TimeFormatPreference {
  return (
    typeof value === "string" &&
    (TIME_FORMAT_PREFERENCES as readonly string[]).includes(value)
  );
}

export function isTimeZoneDisplayPreference(
  value: unknown,
): value is TimeZoneDisplayPreference {
  return (
    typeof value === "string" &&
    (TIME_ZONE_DISPLAY_PREFERENCES as readonly string[]).includes(value)
  );
}

/** Absent document, absent key or an unknown value all resolve to "gym". */
export function effectiveTimeZoneDisplay(
  settings: Pick<UserSettings, "timeZoneDisplay"> | null | undefined,
): TimeZoneDisplayPreference {
  const value = settings?.timeZoneDisplay;
  return isTimeZoneDisplayPreference(value) ? value : DEFAULT_TIME_ZONE_DISPLAY;
}

export function isStorableDeviceTimeZone(zone: unknown): zone is string {
  return (
    isUsableTimeZone(zone) &&
    zone.length <= DEVICE_TIME_ZONE_MAX_LENGTH &&
    DEVICE_TIME_ZONE_PATTERN.test(zone)
  );
}

/** zone null = the gym zone itself is unusable; senders keep their UTC wording. */
export type MessageZone = {
  zone: string | null;
  city: string | null;
  isDevice: boolean;
};

/**
 * The clock a push or email about `atMs` is worded on. The device zone wins
 * only when the member chose it, it is storable and names a city, and its
 * clock reads differently from the gym's at `atMs`; every other case is the
 * gym. Never throws: settings come raw from Firestore.
 */
export function effectiveMessageZone(
  settings: unknown,
  gymZone: unknown,
  atMs: number,
): MessageZone {
  if (!isUsableTimeZone(gymZone))
    return { zone: null, city: null, isDevice: false };
  const gym: MessageZone = {
    zone: gymZone,
    city: zoneCity(gymZone),
    isDevice: false,
  };
  try {
    const { timeZoneDisplay, deviceTimeZone } = Object(settings) as Record<
      string,
      unknown
    >;
    if (
      timeZoneDisplay !== "device" ||
      !isStorableDeviceTimeZone(deviceTimeZone)
    )
      return gym;
    const city = zoneCity(deviceTimeZone);
    if (city === null || !clocksDiffer(deviceTimeZone, gymZone, [atMs]))
      return gym;
    return { zone: deviceTimeZone, city, isDevice: true };
  } catch {
    return gym;
  }
}
